/**
 * Consumer Portal Branding (Sprint C14 Lane B WP-8 + Lane C WP-4)
 *
 * FI-tenant-scoped branding configuration for the consumer portal.
 * Minimal scope: logo URL + welcome copy ONLY. No subdomain, no theme
 * tokens, no brand color picker. Operator pastes a CDN URL for the logo;
 * asset upload to object store is the C14->C15-3 follow-on.
 *
 * Backend: platform-admin-api routes mounted at
 *   GET /api/admin/tenants/me/branding
 *   PUT /api/admin/tenants/me/branding
 *
 * Types: PlatformAdmin.components.schemas.{BrandingConfig,BrandingConfigUpdate}
 * from `@aaimplatform/institutional-types`. No inline interfaces.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2, RotateCcw, Save, Image as ImageIcon } from 'lucide-react';
import { Button, Heading, Skeleton } from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { platformAdminClient } from '../../api/client';

type BrandingConfig = components['schemas']['BrandingConfig'];
type BrandingConfigUpdate = components['schemas']['BrandingConfigUpdate'];

const QUERY_KEY = ['admin', 'tenants', 'me', 'branding'] as const;
const WELCOME_COPY_MAX = 2000;
const LOGO_URL_MAX = 500;
const URL_PATTERN = /^https?:\/\/[^\s]{1,500}$/i;

interface FormState {
  logoUrl: string;
  welcomeCopy: string;
}

function formFromConfig(config: BrandingConfig): FormState {
  return {
    logoUrl: config.logoUrl ?? '',
    welcomeCopy: config.welcomeCopy ?? '',
  };
}

function buildUpdate(form: FormState, baseline: BrandingConfig | undefined): BrandingConfigUpdate {
  const baselineForm = baseline ? formFromConfig(baseline) : { logoUrl: '', welcomeCopy: '' };
  const update: BrandingConfigUpdate = {};
  if (form.logoUrl !== baselineForm.logoUrl) {
    update.logoUrl = form.logoUrl.length === 0 ? null : form.logoUrl;
  }
  if (form.welcomeCopy !== baselineForm.welcomeCopy) {
    update.welcomeCopy = form.welcomeCopy.length === 0 ? null : form.welcomeCopy;
  }
  return update;
}

export function ConsumerPortalBrandingPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({ logoUrl: '', welcomeCopy: '' });
  const [logoUrlError, setLogoUrlError] = useState<string | null>(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const brandingQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<BrandingConfig> => {
      const { data, error } = await platformAdminClient.GET('/tenants/me/branding');
      if (error || !data) throw new Error('Failed to load branding configuration');
      return data;
    },
    staleTime: 30_000,
  });

  // Hydrate the form once initial data arrives.
  useEffect(() => {
    if (brandingQuery.data) {
      setForm(formFromConfig(brandingQuery.data));
    }
  }, [brandingQuery.data]);

  const isDirty = useMemo(() => {
    if (!brandingQuery.data) return false;
    const update = buildUpdate(form, brandingQuery.data);
    return Object.keys(update).length > 0;
  }, [form, brandingQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (): Promise<BrandingConfig> => {
      const body = buildUpdate(form, brandingQuery.data);
      const { data, error } = await platformAdminClient.PUT('/tenants/me/branding', { body });
      if (error || !data) throw new Error('Failed to save branding configuration');
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      setForm(formFromConfig(data));
      setSaveErrorMessage(null);
    },
    onError: (error: Error) => {
      setSaveErrorMessage(error.message);
    },
  });

  const handleLogoUrlChange = (value: string) => {
    setForm((prev) => ({ ...prev, logoUrl: value }));
    if (value.length === 0) {
      setLogoUrlError(null);
      return;
    }
    if (value.length > LOGO_URL_MAX) {
      setLogoUrlError(`Logo URL must be at most ${LOGO_URL_MAX} characters`);
      return;
    }
    if (!URL_PATTERN.test(value)) {
      setLogoUrlError('Logo URL must be an absolute http(s) URL');
      return;
    }
    setLogoUrlError(null);
  };

  const handleWelcomeCopyChange = (value: string) => {
    // Soft truncate to MAX so the counter never reads negative; the backend
    // also enforces this limit defensively.
    const next = value.length > WELCOME_COPY_MAX ? value.slice(0, WELCOME_COPY_MAX) : value;
    setForm((prev) => ({ ...prev, welcomeCopy: next }));
  };

  const handleReset = () => {
    if (brandingQuery.data) {
      setForm(formFromConfig(brandingQuery.data));
    }
    setLogoUrlError(null);
    setSaveErrorMessage(null);
  };

  const canSave = isDirty && !logoUrlError && !saveMutation.isPending;
  const welcomeCopyRemaining = WELCOME_COPY_MAX - form.welcomeCopy.length;

  if (brandingQuery.isLoading) {
    return (
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <Heading level={2}>Consumer Portal Branding</Heading>
          <div className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>
        <Skeleton className="h-40 rounded-sm mb-6" />
        <Skeleton className="h-64 rounded-sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6 sm:gap-6">
        <div>
          <Heading level={2}>Consumer Portal Branding</Heading>
          <div className="text-sm font-sans text-tertiary leading-normal mt-2 max-w-2xl">
            Configure the logo and welcome message shown to your members on the
            Aaim consumer portal. Subdomain customization and asset upload are
            tracked as future enhancements -- paste an absolute CDN URL for the
            logo today.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 flex-shrink-0">
          <Button variant="secondary" onClick={handleReset} disabled={!isDirty || saveMutation.isPending}>
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <Button variant="primary" onClick={() => saveMutation.mutate()} disabled={!canSave}>
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {(brandingQuery.error || saveErrorMessage) && (
        <div className="bg-error/5 border border-error/20 rounded-sm p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
          <div className="text-sm font-sans text-error">
            {brandingQuery.error ? 'Failed to load branding configuration' : saveErrorMessage}
          </div>
        </div>
      )}

      {/* Logo URL */}
      <div className="bg-surface rounded-sm shadow-sm p-6 mb-6">
        <Heading level={3} className="mb-2">Logo</Heading>
        <div className="text-sm font-sans text-tertiary leading-normal mb-6">
          Absolute URL to a CDN-hosted logo (PNG or SVG, max 500 characters).
          Asset upload to object store is forthcoming; for now, host the file
          on your existing CDN and paste the public URL here.
        </div>

        <label className="text-sm font-sans font-medium text-primary leading-normal block mb-2">
          Logo URL
        </label>
        <input
          type="url"
          value={form.logoUrl}
          onChange={(e) => handleLogoUrlChange(e.target.value)}
          placeholder="https://cdn.example.com/logo.png"
          maxLength={LOGO_URL_MAX}
          className={`w-full px-3 py-2 border rounded-sm text-sm font-mono focus:outline-none focus:border-accent ${
            logoUrlError ? 'border-error' : 'border-recessed'
          }`}
        />
        {logoUrlError && (
          <div className="text-xs font-sans text-error leading-normal mt-2">{logoUrlError}</div>
        )}

        {form.logoUrl && !logoUrlError && (
          <div className="mt-4 p-3 border border-recessed rounded-sm bg-elevated">
            <div className="text-xs font-sans font-semibold uppercase text-tertiary mb-2">
              Preview
            </div>
            <div className="flex items-center gap-3">
              <img
                src={form.logoUrl}
                alt="Your logo preview"
                className="h-12 max-w-xs object-contain bg-surface border border-recessed rounded-sm p-1"
                onError={(e) => {
                  // If the URL fails to load (broken link, CORS, etc.), swap
                  // to a placeholder icon so the preview still renders.
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const next = target.nextElementSibling as HTMLElement | null;
                  if (next) next.style.display = 'flex';
                }}
              />
              <div
                style={{ display: 'none' }}
                className="h-12 w-32 items-center justify-center bg-surface border border-recessed rounded-sm text-xs font-sans text-tertiary"
              >
                <ImageIcon className="w-4 h-4 mr-1" /> Failed to load
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Welcome Copy */}
      <div className="bg-surface rounded-sm shadow-sm p-6">
        <Heading level={3} className="mb-2">Welcome Copy</Heading>
        <div className="text-sm font-sans text-tertiary leading-normal mb-6">
          Short welcome paragraph shown to your members on the consumer portal
          landing and pledge confirmation surfaces. Plain text only; markdown
          and HTML are not rendered.
        </div>

        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-sans font-medium text-primary leading-normal">
            Welcome message
          </label>
          <div
            className={`text-xs font-sans leading-normal ${
              welcomeCopyRemaining < 100 ? 'text-error' : 'text-tertiary'
            }`}
          >
            {welcomeCopyRemaining} characters remaining
          </div>
        </div>
        <textarea
          value={form.welcomeCopy}
          onChange={(e) => handleWelcomeCopyChange(e.target.value)}
          placeholder="Welcome to your member-only collateral pledge portal..."
          rows={6}
          maxLength={WELCOME_COPY_MAX}
          className="w-full px-3 py-2 border border-recessed rounded-sm text-sm font-sans focus:outline-none focus:border-accent resize-y"
        />
      </div>
    </div>
  );
}
